// oxlint-disable import/default -- Vite 的 ?raw 导入返回源码字符串，lint 无法识别默认导出
import type { ModuleKey } from "@/types/office";
import type { StepKind } from "@/office/office-context";
import docEditorSource from "@/office/DocEditor.tsx?raw";
import officeContextSource from "@/office/office-context.tsx?raw";
import sheetEditorSource from "@/office/SheetEditor.tsx?raw";
import sheetEngineSource from "@/lib/sheet-engine.ts?raw";

export type SourceFileKey = "context" | "doc" | "sheet" | "engine";

export const SOURCE_FILES: Record<SourceFileKey, { path: string; short: string; source: string }> = {
  context: { path: "src/office/office-context.tsx", short: "office-context.tsx", source: officeContextSource },
  doc: { path: "src/office/DocEditor.tsx", short: "DocEditor.tsx", source: docEditorSource },
  sheet: { path: "src/office/SheetEditor.tsx", short: "SheetEditor.tsx", source: sheetEditorSource },
  engine: { path: "src/lib/sheet-engine.ts", short: "sheet-engine.ts", source: sheetEngineSource },
};

export type StepRegion = {
  fileKey: SourceFileKey;
  region: string;
  caption: string;
};

export type StepSpec = {
  module: ModuleKey;
  summary: string;
  regions: StepRegion[];
};

export const STEP_SPECS: Record<StepKind, StepSpec> = {
  "doc.text": {
    module: "doc",
    summary: "contentEditable 的 innerText 被写回 store；输入停顿 0.9 秒或失焦后，整次修改记为一步。",
    regions: [
      { fileKey: "context", region: "action.text", caption: "状态写入口 · 文本合并与步骤记录" },
      { fileKey: "doc", region: "ui.blocks", caption: "编辑区 · 段落渲染与输入回写" },
    ],
  },
  "doc.format": {
    module: "doc",
    summary: "工具栏只发出一次 patch，状态合并与步骤记录都发生在 store，因此界面与源码一一对应。",
    regions: [
      { fileKey: "context", region: "action.format", caption: "状态写入口 · 字重切换" },
      { fileKey: "doc", region: "ui.format", caption: "工具栏 · 加粗 / 倾斜按钮" },
    ],
  },
  "doc.blockType": {
    module: "doc",
    summary: "段落类型是纯数据，切换类型只改一个字段，渲染层按类型选择排版。",
    regions: [
      { fileKey: "context", region: "action.blockType", caption: "状态写入口 · 段落类型" },
      { fileKey: "doc", region: "ui.types", caption: "工具栏 · 标题与列表" },
    ],
  },
  "doc.insert": {
    module: "doc",
    summary: "新建段落插入到当前段落之后，新块的 id 由计数器生成，并立即成为选中块。",
    regions: [
      { fileKey: "context", region: "action.insert", caption: "状态写入口 · 插入空段落" },
      { fileKey: "doc", region: "ui.insert", caption: "工具栏 · 新建段落按钮" },
    ],
  },
  "doc.table": {
    module: "doc",
    summary: "表格作为独立块类型存入文档流，插入时带一份默认表头，渲染层按 rows 展开。",
    regions: [
      { fileKey: "context", region: "action.table", caption: "状态写入口 · 插入表格块" },
      { fileKey: "doc", region: "ui.table", caption: "工具栏 · 插入表格按钮" },
    ],
  },
  "doc.remove": {
    module: "doc",
    summary: "删除只改动 blocks 数组，选中状态自动回退到第一个块，避免出现无选中的悬空状态。",
    regions: [
      { fileKey: "context", region: "action.remove", caption: "状态写入口 · 移除段落" },
      { fileKey: "doc", region: "ui.remove", caption: "工具栏 · 删除按钮" },
    ],
  },
  "sheet.cell": {
    module: "sheet",
    summary: "单元格保存原始输入，显示值由公式引擎实时计算，因此源码里能同时看到写入与求值两条链路。",
    regions: [
      { fileKey: "context", region: "action.cell", caption: "状态写入口 · 单元格写入" },
      { fileKey: "engine", region: "engine.evaluate", caption: "公式引擎 · 显示值计算" },
      { fileKey: "sheet", region: "ui.grid", caption: "表格视图 · 单元格渲染" },
    ],
  },
  "sheet.header": {
    module: "sheet",
    summary: "表头字重是表格级样式开关，一次切换会重渲染整行表头。",
    regions: [
      { fileKey: "context", region: "action.header", caption: "状态写入口 · 表头字重" },
      { fileKey: "sheet", region: "ui.header", caption: "工具栏 · 表头加粗按钮" },
    ],
  },
  "sheet.row": {
    module: "sheet",
    summary: "行数只是渲染范围，新增行不会破坏已有单元格，数据仍按 A1 形式的引用存放。",
    regions: [
      { fileKey: "context", region: "action.row", caption: "状态写入口 · 扩展行数" },
      { fileKey: "sheet", region: "ui.row", caption: "工具栏 · 新增行按钮" },
    ],
  },
};

export type RegionSlice = {
  code: string;
  startLine: number;
  endLine: number;
  markerStart: number;
  markerEnd: number;
};

export function extractRegion(fileKey: SourceFileKey, region: string): RegionSlice | null {
  const lines = SOURCE_FILES[fileKey].source.split("\n");
  const start = lines.findIndex((line) => line.includes(`#region ${region}`));
  if (start < 0) return null;
  const end = lines.findIndex((line, index) => index > start && line.includes(`#endregion ${region}`));
  if (end < 0) return null;

  const content = lines.slice(start + 1, end);
  while (content.length > 0 && content[0].trim() === "") content.shift();
  while (content.length > 0 && content[content.length - 1].trim() === "") content.pop();

  return {
    code: content.join("\n"),
    startLine: start + 2,
    endLine: end,
    markerStart: start + 1,
    markerEnd: end + 1,
  };
}

export type StepCodeBlock = {
  fileKey: SourceFileKey;
  path: string;
  caption: string;
  code: string;
  startLine: number;
};

export function getStepBlocks(kind: StepKind): StepCodeBlock[] {
  return STEP_SPECS[kind].regions.flatMap((region) => {
    const slice = extractRegion(region.fileKey, region.region);
    if (!slice) return [];
    return [
      {
        fileKey: region.fileKey,
        path: SOURCE_FILES[region.fileKey].path,
        caption: region.caption,
        code: slice.code,
        startLine: slice.startLine,
      },
    ];
  });
}

export function getStepFileRanges(kind: StepKind): { fileKey: SourceFileKey; start: number; end: number }[] {
  return STEP_SPECS[kind].regions.flatMap((region) => {
    const slice = extractRegion(region.fileKey, region.region);
    if (!slice) return [];
    return [{ fileKey: region.fileKey, start: slice.markerStart, end: slice.markerEnd }];
  });
}
